import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import api from '../services/api';

import { IOption } from '../components/SelectSync';
import { useToast } from './toast';
import getResponseErrors from '../utils/getResponseErrors';

export interface ICategoria {
  CatCodigo: number;
  CatNome: string;
  CatPai: number;
  CatReceita: boolean;
  CatAtivo: boolean;
}

export interface ICategorias {
  despesa: ICategoria[];
  receita: ICategoria[];
  outrasReceitas: ICategoria[];
}

export interface IOptionsCategorias {
  despesa: IOption[];
  receita: IOption[];
  outrasReceitas: IOption[];
}

interface CategoriaContextData {
  loading: boolean;
  optionCategorias: IOptionsCategorias;
  categorias: ICategorias;
  buscaCategoria(): Promise<void>;
  limparCategoria(): void;
}

const CategoriaContext = createContext<CategoriaContextData>(
  {} as CategoriaContextData,
);

const CategoriaProvider: React.FC = ({ children }) => {
  const [loading, setLoading] = useState(false);

  const { addToast } = useToast();

  const [categorias, setCategorias] = useState<ICategorias>(() => {
    const categs = localStorage.getItem('@AgendaFacil:categorias');
    if (categs) return JSON.parse(categs);

    return {} as ICategorias;
  });

  const [optionCategorias, setOptionCategorias] = useState<IOptionsCategorias>(
    () => {
      const optionCategs = localStorage.getItem(
        '@AgendaFacil:optionCategorias',
      );
      if (optionCategs) return JSON.parse(optionCategs);

      return {} as IOptionsCategorias;
    },
  );

  const buscaCategoria = useCallback(async () => {
    setLoading(true);
    try {
      const responseReceita = await api.get<ICategoria[]>(
        `/categoria?CatAtivo=true&CatReceita=1`,
      );

      const responseDespesa = await api.get<ICategoria[]>(
        `/categoria?CatAtivo=true&CatReceita=0`,
      );

      const codigoOutrasReceitas = responseReceita.data.find(
        cat => cat.CatNome === 'OUTRAS RECEITAS',
      )?.CatCodigo;

      const receitasClinica = responseReceita.data.filter(cat => {
        if (codigoOutrasReceitas) {
          return (
            cat.CatNome !== 'OUTRAS RECEITAS' &&
            cat.CatPai !== codigoOutrasReceitas
          );
        }

        return cat.CatNome !== 'OUTRAS RECEITAS';
      });

      const outrasReceitas = responseReceita.data.filter(cat => {
        if (codigoOutrasReceitas) {
          return cat.CatPai === codigoOutrasReceitas;
        }
        return false;
      });

      const optsReceita: IOption[] = receitasClinica.map((op: ICategoria) => ({
        label: op.CatNome,
        value: op.CatCodigo.toString(),
      }));

      const optsOutrasReceitas: IOption[] = outrasReceitas.map(
        (op: ICategoria) => ({
          label: op.CatNome,
          value: op.CatCodigo.toString(),
        }),
      );

      const optsDespesa: IOption[] = responseDespesa.data.map(
        (op: ICategoria) => ({
          label: op.CatNome,
          value: op.CatCodigo.toString(),
        }),
      );

      const localCategorias = {
        receita: receitasClinica,
        despesa: responseDespesa.data,
        outrasReceitas,
      };

      const localOptions = {
        receita: optsReceita,
        despesa: optsDespesa,
        outrasReceitas: optsOutrasReceitas,
      };

      localStorage.setItem(
        '@AgendaFacil:categorias',
        JSON.stringify(localCategorias),
      );
      localStorage.setItem(
        '@AgendaFacil:optionCategorias',
        JSON.stringify(localOptions),
      );
      setCategorias(localCategorias);

      setOptionCategorias(localOptions);
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível carregar categorias',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    async function load(): Promise<void> {
      const tokenLocal = localStorage.getItem('@AgendaFacil:token');
      if (tokenLocal) {
        await buscaCategoria();
      }
    }
    load();
  }, [buscaCategoria]);

  const limparCategoria = useCallback(() => {
    localStorage.removeItem('@AgendaFacil:optionCategorias');
    localStorage.removeItem('@AgendaFacil:categorias');
    setCategorias({} as ICategorias);
    setOptionCategorias({} as IOptionsCategorias);
  }, []);
  return (
    <CategoriaContext.Provider
      value={{
        loading,
        categorias,
        optionCategorias,
        buscaCategoria,
        limparCategoria,
      }}
    >
      {children}
    </CategoriaContext.Provider>
  );
};

function useCategoria(): CategoriaContextData {
  const context = useContext(CategoriaContext);

  if (!context) {
    throw new Error('useCategoria deve ser usado com o CategoriaProvider');
  }

  return context;
}

export { CategoriaProvider, useCategoria };
